import React, { useState, useMemo } from 'react';
import {
  COMPARABLE_METRICS, num, changeDirection, directionMeta, scoreColor,
  attemptLabel, attemptSubLabel, APT,
} from './aptitudeHelpers';

// Feature 1 — Compare any two completed aptitude attempts, side by side.
// Reads exclusively from the stored per-attempt rows (history). Nothing is
// recomputed; we only diff the stored scalar metrics + stored strengths/weaknesses.

const listDiff = (fromArr, toArr) => {
  const norm = (x) => (typeof x === 'string' ? x : x?.topic_name || x?.area || JSON.stringify(x));
  const a = new Set((fromArr || []).map(norm));
  const b = new Set((toArr || []).map(norm));
  const added = [...b].filter((x) => !a.has(x));
  const removed = [...a].filter((x) => !b.has(x));
  const same = [...b].filter((x) => a.has(x));
  return { added, removed, same };
};

const MiniBar = ({ value, color }) => (
  <div className="apt-cmp-mini">
    <div className="apt-cmp-minibar" style={{ width: `${Math.max(num(value) || 0, 2)}%`, background: color }} />
    <span className="apt-cmp-num" style={{ color }}>{num(value) == null ? '—' : `${Math.round(num(value))}`}</span>
  </div>
);

const AptitudeCompare = ({ history }) => {
  const rows = history || [];
  const sorted = useMemo(() => [...rows].sort((a, b) => (a.attempt_number || 0) - (b.attempt_number || 0)), [rows]);

  const [aId, setAId] = useState(sorted[0]?.attempt_id || '');
  const [bId, setBId] = useState(sorted[sorted.length - 1]?.attempt_id || '');

  if (sorted.length < 2) {
    return (
      <div className="apt-empty">
        <span className="i">⚖️</span>
        <h3 style={{ margin: '0 0 6px', color: '#0f172a' }}>Need at least two attempts</h3>
        <p style={{ margin: 0 }}>Complete two or more aptitude tests to compare them side by side.</p>
      </div>
    );
  }

  const A = sorted.find((r) => r.attempt_id === aId) || sorted[0];
  const B = sorted.find((r) => r.attempt_id === bId) || sorted[sorted.length - 1];

  const tally = COMPARABLE_METRICS.reduce((acc, m) => {
    const dir = changeDirection(A[m.key], B[m.key]);
    if (dir === 'up') acc.improved++; else if (dir === 'down') acc.declined++; else if (dir === 'flat') acc.same++;
    return acc;
  }, { improved: 0, declined: 0, same: 0 });

  const strengthDiff = listDiff(A.strengths, B.strengths);
  const weaknessDiff = listDiff(A.weaknesses, B.weaknesses);

  const Option = ({ r }) => <option value={r.attempt_id}>{attemptLabel(r)} — {attemptSubLabel(r)}</option>;

  return (
    <>
      <div className="apt-card" style={{ marginBottom: 22 }}>
        <div className="apt-select-row">
          <div className="apt-select-field">
            <label>Baseline attempt</label>
            <select className="apt-select" value={aId} onChange={(e) => setAId(e.target.value)}>
              {sorted.map((r) => <Option key={r.attempt_id} r={r} />)}
            </select>
          </div>
          <span className="apt-vs">vs</span>
          <div className="apt-select-field">
            <label>Comparison attempt</label>
            <select className="apt-select" value={bId} onChange={(e) => setBId(e.target.value)}>
              {sorted.map((r) => <Option key={r.attempt_id} r={r} />)}
            </select>
          </div>
        </div>
      </div>

      {/* Summary tally */}
      <div className="apt-cmp-summary">
        <div className="apt-cmp-pill" style={{ borderColor: '#bbf7d0' }}>
          <div className="n" style={{ color: APT.up }}>{tally.improved}</div><div className="t">Improved</div>
        </div>
        <div className="apt-cmp-pill" style={{ borderColor: '#fecdd3' }}>
          <div className="n" style={{ color: APT.down }}>{tally.declined}</div><div className="t">Declined</div>
        </div>
        <div className="apt-cmp-pill">
          <div className="n" style={{ color: APT.flat }}>{tally.same}</div><div className="t">No Change</div>
        </div>
      </div>

      {/* Metric-by-metric table */}
      <div className="apt-card">
        <table className="apt-cmp-table">
          <thead>
            <tr>
              <th>Metric</th>
              <th className="num hide-sm">{attemptLabel(A)}</th>
              <th className="num hide-sm">{attemptLabel(B)}</th>
              <th className="num">Change</th>
            </tr>
          </thead>
          <tbody>
            {COMPARABLE_METRICS.map((m) => {
              const from = num(A[m.key]);
              const to = num(B[m.key]);
              const dir = changeDirection(from, to);
              const dm = directionMeta(dir);
              const diff = from != null && to != null ? (to - from) : null;
              return (
                <tr key={m.key}>
                  <td>
                    <div className="apt-cmp-metric">
                      <span className="ic">{m.icon}</span>{m.label}
                    </div>
                  </td>
                  <td className="num hide-sm"><MiniBar value={from} color={scoreColor(from)} /></td>
                  <td className="num hide-sm"><MiniBar value={to} color={scoreColor(to)} /></td>
                  <td className="num">
                    <span className="apt-delta-tag" style={{ color: dm.color, background: `${dm.color}14` }}>
                      {dm.arrow} {diff == null ? '—' : `${dm.sign}${Math.abs(Math.round(diff))}`}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Qualitative diffs */}
      <div className="apt-cmp-lists">
        <div className="apt-card">
          <h3 className="apt-section-title" style={{ marginBottom: 12 }}>Strengths — What Changed</h3>
          <DiffList diff={strengthDiff} addLabel="Gained" removeLabel="Lost" keepLabel="Kept" />
        </div>
        <div className="apt-card">
          <h3 className="apt-section-title" style={{ marginBottom: 12 }}>Weaknesses — What Changed</h3>
          <DiffList diff={weaknessDiff} addLabel="New" removeLabel="Resolved" keepLabel="Still" invert />
        </div>
      </div>
    </>
  );
};

// For strengths: added = good (green). For weaknesses: added = bad, removed = good (invert tone).
const DiffList = ({ diff, addLabel, removeLabel, keepLabel, invert }) => {
  const items = [
    ...diff.added.map((x) => ({ x, cls: invert ? 'removed' : 'added', tag: addLabel })),
    ...diff.removed.map((x) => ({ x, cls: invert ? 'added' : 'removed', tag: removeLabel })),
    ...diff.same.map((x) => ({ x, cls: 'same', tag: keepLabel })),
  ];
  if (items.length === 0) return <p className="apt-muted">No items recorded for these attempts.</p>;
  return (
    <ul className="apt-diff-list">
      {items.map((it, i) => (
        <li className={it.cls} key={i}>
          <span className="apt-diff-tag" style={{
            background: it.cls === 'added' ? 'rgba(5,150,105,0.15)' : it.cls === 'removed' ? 'rgba(225,29,72,0.14)' : '#e2e8f0',
            color: it.cls === 'added' ? APT.up : it.cls === 'removed' ? APT.down : '#64748b',
          }}>{it.tag}</span>
          {it.x}
        </li>
      ))}
    </ul>
  );
};

export default AptitudeCompare;
